// Pure helpers for the radio player (Brief 4 / P4a). No React, no I/O — the
// spend-meter math + the spoken-summary composition, so the money-facing logic
// is unit-tested without a DOM. The SERVER is authoritative on spend; these only
// FORMAT what the server returns and COMPOSE the operator-facing text to voice.

import type { TraceEvent } from "@waytide/core";

/** Voice at most the most-recent N trace summaries — bounds a clip's length
 * (and so its cost) on the client side too (the server caps again at 4500). */
const MAX_SUMMARIES = 5;

/** Format micros (1e6 = $1.00) as a calm dollar string: 500000 → "$0.50". */
export function formatMicros(micros: number): string {
  return `$${(Math.max(0, micros) / 1_000_000).toFixed(2)}`;
}

/** The spend-meter fill fraction (0..1): how much of the cap is SPENT. */
export function meterFraction(capMicros: number, remainingMicros: number): number {
  if (capMicros <= 0) return 0;
  const spent = capMicros - Math.max(0, Math.min(capMicros, remainingMicros));
  return Math.max(0, Math.min(1, spent / capMicros));
}

/** Whether the budget is low enough for the calm heads-up — at/under
 * `thresholdPct` of the cap consumed. Never alarms; just informs. */
export function isLowBudget(
  capMicros: number,
  remainingMicros: number,
  thresholdPct: number,
): boolean {
  if (capMicros <= 0) return false;
  const usedPct = (1 - Math.max(0, remainingMicros) / capMicros) * 100;
  return usedPct >= thresholdPct;
}

/** Turn one trace entry into a plain-English clause for the voice — attributing
 * the Operators BY NAME (so "Supervisor paused it" reads as the alignment event it is)
 * and stating the agent's actions in everyday words. `kind` is an open
 * vocabulary; an unknown kind falls back to its own summary, which already reads
 * as a human one-liner. Pure. */
function phraseEvent(kind: string, summary: string): string {
  switch (kind) {
    case "operator_halt":
      return `Supervisor paused the work — ${summary}`;
    case "operator_flag":
      return `Supervisor flagged a concern — ${summary}`;
    case "suggestion":
      // The trace kind doesn't say WHICH of the three arbiters spoke, so the
      // voice attributes it to the role, not a name.
      return `an arbiter suggested ${summary}`;
    case "reach":
      return `it reached out — ${summary}`;
    case "commit":
      return `it committed ${summary}`;
    case "proof":
      return `a check ran — ${summary}`;
    case "artifact":
      return `it produced ${summary}`;
    default:
      // chat + any unknown kind: the summary is already the human one-liner.
      return summary;
  }
}

/** The chronological lead-in for the clause at `idx` of `total` (total ≥ 2):
 * the first beat is "First,", the last "Most recently,", and the middle beats
 * cycle so a long recap doesn't drone "Then. Then. Then." Pure. */
const MIDDLE_CONNECTIVES = ["Then,", "After that,", "Next,"];
function connective(idx: number, total: number): string {
  if (idx === 0) return "First,";
  if (idx === total - 1) return "Most recently,";
  return MIDDLE_CONNECTIVES[(idx - 1) % MIDDLE_CONNECTIVES.length];
}

/** End a clause with sentence punctuation so the voice phrases each as its own
 * beat (no double-up if it already ends in `.`/`!`/`?`). */
function endSentence(s: string): string {
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** Compose the operator-facing script to voice for a session: a CLEAR,
 * CHRONOLOGICAL STATUS UPDATE — what really happened, in what order — for a
 * human with little time (Brief 4 / P8). Not a log dump: the events are sorted
 * oldest→newest (defensively, since some callers hand us display-ordered,
 * newest-first trace), turned into plain-English clauses that name the Operators
 * who acted, and strung together with "First… Then… Most recently…" so the
 * order is unmistakable when heard. Bounded to the most recent few so the clip
 * cost stays small. Operator-facing audio, so NO never-reveal redaction (that
 * gate is for agent-facing text). Calm, factual — no fabrication. Pure. */
export function composeSpoken(events: TraceEvent[], label: string): string {
  // Chronological is the whole point ("in what order"): sort oldest→newest with
  // a stable tie-break, so same-stamp events keep their arrival order.
  const ordered = events
    .map((e, i) => ({ e, i }))
    .sort((a, b) =>
      a.e.created_at !== b.e.created_at
        ? a.e.created_at - b.e.created_at
        : a.i - b.i,
    )
    .map((w) => w.e);

  // One clean clause per event; drop blanks and collapse a clause that merely
  // repeats the one before it (agents often log the same beat twice).
  const clauses: string[] = [];
  for (const ev of ordered) {
    const summary = (ev.summary || "").trim();
    if (!summary) continue;
    const clause = phraseEvent(ev.kind, summary);
    if (clause !== clauses[clauses.length - 1]) clauses.push(clause);
  }

  if (clauses.length === 0) {
    return `Here's your update on ${label}. Nothing's been logged yet, so it's quiet for now.`;
  }

  // Keep it short for a busy listener: the most recent few, still in order.
  const recent = clauses.slice(-MAX_SUMMARIES);
  if (recent.length === 1) {
    return `Here's the latest from ${label}: ${endSentence(recent[0])} That's where it stands.`;
  }
  const body = recent
    .map((clause, idx) => `${connective(idx, recent.length)} ${endSentence(clause)}`)
    .join(" ");
  return `Here's your update on ${label} — the last ${recent.length} steps, in order. ${body} That's where it stands.`;
}
